<template>
  <div class="webglsnapshot">
    <div class="webglsnapshot-container"
         v-ratio="{ratio: 1920 / (1080-(284)*2)}">
      <div class="webglsnapshot-middle">
        <canvas ref="canvas"
                style="width:100%"
                v-ratio="{ratio: 1920 / 1080, cb: resize}"
                v-if="errorMsg===''"
                @mousedown="canvasMouseDown"
                @mouseup="canvasMouseUp"
                @mousewheel="canvasMouseWheel"
                @mousemove="canvasMouseMove"
                @mouseleave="canvasMouseLeave"></canvas>
        <div v-else
             v-html="errorMsg">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as PIXI from 'pixi.js'
import storejs from 'store'
import Tools from './tools'
import Render from './render'
import LayerName from './layername'
import Detect from './detect'
import api from '@/api'

PIXI.utils.skipHello()
const defaultConfig = {
  width: 1920,
  height: 1080,
  box: { top: 284, right: 0, bottom: 284, left: 0 },
}
let PIXIApp = null
/** @type {PIXI['Container']} */
let PIXIStage = null
let PIXILayersContainer = null
let PIXIToolsContainer = null

export default {
  props: {
    baseLayer: {
      required: true,
    },
    mode: {
      default: 'view',
    },
    visiableLayers: {
      default: () => [],
    },
    layerData: {
      default: () => [],
    },
    tools: {
      default: () => [],
    },
    options: {
      default: () => ({}),
    },
  },
  data() {
    return {
      width: 0,
      height: 0,
      mouseStart: null,
      mouseNow: null,

      errorMsg: '',
    }
  },
  computed: {
    mergedOptions() {
      return Object.assign(defaultConfig, this.options)
    },
  },
  mounted() {
    Detect()
      .then(() => {
        this.errorMsg = ''
        this.initPIXI()
        this.setBaseLayer(this.baseLayer)
        this.drawLayer(this.layerData)
        // ANALYSE:
        this.$refs.canvas.addEventListener(
          'webglcontextlost',
          event => {
            api.log({
              type: 'label:lossWebGLContext',
              priority: 'error',
              addr: window.location.href,
              user: storejs.get('user').UserName,
              message: window.navigator.appVersion,
            })
          },
          false,
        )
      })
      .catch(e => {
        this.errorMsg = e.message
      })
  },
  destroyed() {
    PIXIApp.destroy(true, {
      children: true,
      texture: true,
      baseTexture: true,
    })
    PIXIApp = null
    PIXIStage = null
    PIXILayersContainer = null
    PIXIToolsContainer = null
  },
  methods: {
    resize(size) {
      this.width = size[0]
      this.height = size[1]
      this.updateTool()
    },
    initPIXI() {
      PIXIApp = new PIXI.Application({
        view: this.$refs.canvas,
        width: this.mergedOptions.width,
        height: this.mergedOptions.height,
      })
      PIXIApp.renderer.backgroundColor = 0xffffff
      PIXIStage = PIXIApp.stage

      // loading text
      const loadingText = new PIXI.Text('暂无图像数据', {
        fontFamily: 'Arial',
        fontSize: 36,
        fill: '#409EFF',
      })
      loadingText.position.set(
        this.mergedOptions.width / 2 - loadingText.width / 2,
        this.mergedOptions.height / 2 - loadingText.height / 2,
      )
      PIXIStage.addChild(loadingText)

      // base layer
      const baseLayerSprite = new PIXI.Sprite()
      baseLayerSprite.name = LayerName.baseLayer
      PIXIStage.addChild(baseLayerSprite)

      const layersContainer = new PIXI.Container()
      layersContainer.name = LayerName.layerContainer
      layersContainer.zIndex = 1
      PIXILayersContainer = layersContainer
      PIXIStage.addChild(layersContainer)

      const toolsContainer = new PIXI.Container()
      toolsContainer.name = LayerName.tools
      toolsContainer.zIndex = 2
      PIXIToolsContainer = toolsContainer
      PIXIStage.addChild(toolsContainer)
      this.updateTool()
    },

    canvasMouseDown(e) {
      this.mouseStart = Object.freeze(e)
      Tools.mouseDown(e)
    },
    canvasMouseUp(e) {
      Tools.mouseUp(e)
    },
    canvasMouseWheel(e) {
      Tools.mouseWheel(e)
    },
    canvasMouseMove(e) {
      this.mouseNow = Object.freeze(e)
      Tools.mouseMove(e)
    },
    canvasMouseLeave(e) {
      Tools.mouseLeave(e)
    },
    setBaseLayer(val) {
      const baseLayerSprite = PIXIStage.getChildByName(LayerName.baseLayer)
      if (val && val.source) {
        let base = new PIXI.BaseTexture.fromBuffer(
          val.source,
          val.width,
          val.height,
          {
            format: PIXI.FORMATS.LUMINANCE,
          },
        )
        PIXI.Texture.removeFromCache(baseLayerSprite.texture)
        baseLayerSprite.texture.destroy(true)
        baseLayerSprite.texture = new PIXI.Texture(base)
        baseLayerSprite.visible = true
        this.updateTool()
      } else {
        baseLayerSprite.visible = false
      }
    },
    drawLayer(val) {
      if (val && val.length > 0) {
        val.forEach(layer => {
          const show = this.visiableLayers.indexOf(layer.type) !== -1
          let curLayer = PIXILayersContainer.getChildByName(layer.type)
          if (curLayer) {
            if (show) {
              curLayer.visible = true
              Render(layer, this.mergedOptions, curLayer, PIXIApp.renderer)
            } else {
              curLayer.visible = false
            }
          } else {
            // TODO: 代码结构可以再优化
            curLayer = new PIXI.Container()
            curLayer.name = layer.type
            PIXILayersContainer.addChild(curLayer)
            if (show) {
              curLayer.visible = true
              Render(layer, this.mergedOptions, curLayer, PIXIApp.renderer)
            } else {
              // or remove layer
              curLayer.visible = false
              Render(layer, this.mergedOptions, curLayer, PIXIApp.renderer)
            }
          }
        })
      }
    },
    updateTool(configs) {
      Tools.update(
        this.mouseNow,
        configs || this.tools,
        PIXIToolsContainer,
        PIXIApp,
        {
          width: this.width,
          height: this.height,
          rootOptions: this.mergedOptions,
        },
        this,
      )
    },
  },
  watch: {
    baseLayer(val) {
      this.setBaseLayer(val)
    },
    visiableLayers(val) {
      if (val && val.length > 0) {
        PIXILayersContainer.children.forEach(pixiObj => {
          if (val.indexOf(pixiObj.name) === -1) {
            // hide layer
            pixiObj.visible = false
          } else {
            // show layer
            pixiObj.visible = true
          }
        })
        this.drawLayer(this.layerData)
      }
    },
    layerData(val) {
      this.drawLayer(val)
    },
    tools(configs) {
      this.updateTool(configs)
    },
  },
}
</script>

<style lang="scss" scoped>
.webglsnapshot {
  width: 100%;
  max-width: 1920px;
  position: relative;
  overflow: hidden;
  &-container {
    width: 100%;
  }
  &-middle {
    width: 100%;
    height: 300%;
    position: relative;
    top: -100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}
</style>
